import { Candidate, CandidatesByRole } from "./CandidatesPage.types"
import type {
    Official,
    OfficialAction,
} from "QuorumGrassroots/widgets/ReusableComponents/OfficialCard/OfficialCard.types"

/**
 * Groups candidates by their role/title.
 * Creates a catalog of candidates organized by their legislative level and title.
 *
 * @param candidates - Array of candidate objects
 * @returns Array of objects with role name and associated candidates
 */
export const catalogCandidatesByRole = (candidates: Candidate[]): CandidatesByRole[] => {
    const roleMap = new Map<string, Candidate[]>()

    candidates.forEach((candidate) => {
        const roleName = candidate?.candidate?.title
        if (!roleName) return

        if (!roleMap.has(roleName)) {
            roleMap.set(roleName, [])
        }

        const roleCandidates = roleMap.get(roleName)
        if (roleCandidates) {
            roleCandidates.push(candidate)
        }
    })

    return Array.from(roleMap.entries()).map(([name, candidates]) => ({
        name,
        candidates,
    }))
}

/**
 * Converts a candidate object to OfficialCard props format
 *
 * @param candidate - Candidate object from API
 * @returns Official object for OfficialCard component
 */
export const buildOfficialCardProps = (candidate: Candidate): Official => {
    const partyLower = candidate.party?.toLowerCase() || "other"
    let party: "democrat" | "republican" | "independent" | "other" = "other"

    if (partyLower.includes("dem")) {
        party = "democrat"
    } else if (partyLower.includes("rep")) {
        party = "republican"
    } else if (partyLower.includes("ind")) {
        party = "independent"
    }

    return {
        id: candidate.id,
        name: candidate.full_name,
        party,
        role: candidate.candidate?.title || "Candidate",
        photoUrl: candidate.image_url,
    }
}

/**
 * Builds action buttons for a candidate card
 * Order: Facebook, Twitter, Instagram, YouTube, Contact, Website (last)
 *
 * @param candidate - Candidate object from API
 * @returns Array of OfficialAction objects
 */
export const buildCandidateActions = (candidate: Candidate): OfficialAction[] => {
    const actions: OfficialAction[] = []

    if (candidate.facebook) {
        actions.push({
            type: "facebook",
            label: "Facebook",
            url: candidate.facebook,
        })
    }

    if (candidate.twitter) {
        actions.push({
            type: "twitter",
            label: "Twitter",
            url: candidate.twitter,
        })
    }

    if (candidate.instagram) {
        actions.push({
            type: "instagram",
            label: "Instagram",
            url: candidate.instagram,
        })
    }

    if (candidate.linkedin) {
        actions.push({
            type: "linkedin",
            label: "LinkedIn",
            url: candidate.linkedin,
        })
    }

    if (candidate.youtube) {
        actions.push({
            type: "youtube",
            label: "YouTube",
            url: candidate.youtube,
        })
    }

    if (candidate.contact_website) {
        actions.push({
            type: "contact",
            label: "Contact",
            url: candidate.contact_website,
        })
    }

    // Website is always last
    if (candidate.website) {
        actions.push({
            type: "website",
            label: "Website",
            url: candidate.website,
        })
    }

    return actions
}
